import { EventEmitter } from './event-emitter';
import { Storage } from './storage';
import {
  AccessTokenCookie,
  AccessTokenExpiresInCookie,
} from '@ha/auth/common/cookie';
import { addSeconds, Timer } from './timer';

type TokenEvent = 'loaded' | 'error';

export class Token extends EventEmitter<TokenEvent> {
  private token?: JWT;
  private storage: Storage = new Storage();
  private timer = new Timer();
  private unsubscribe: (() => void) | undefined;

  /**
   * @param cookie The cookie string to parse. Optional, if not provided, the document.cookie will be used.
   */
  private constructor() {
    super(['loaded', 'error']);
  }

  static getNew() {
    return new Token();
  }

  load(cookie?: string) {
    const _cookie = cookie || document.cookie;

    const token = AccessTokenCookie.parse(_cookie) as JWT;
    const expires_in = AccessTokenExpiresInCookie.parse(_cookie);

    if (!token || !expires_in || !isPseudoJWT(token)) {
      // If there's no access_token, should we try calling the refresh endpoint?
      // It might be that a refresh token exists, but the access token has expired.
      this.emit('error', 'token not found or invalid');
      return;
    }

    const expirationDate = addSeconds(expires_in);

    this.token = token;
    const expISOString = this.storage.load(
      {
        prefix: '__ha_exp',
        suffix: uniqueTokenId(token),
      },
      expirationDate.toISOString(),
    );

    const timerDate = expISOString ? new Date(expISOString) : expirationDate;

    this.unsubscribe?.();
    this.unsubscribe = this.timer.on('expiring', this.refresh.bind(this));
    this.timer.start(15 * 1000, timerDate, 60);

    this.emit('loaded', { token, expirationDate: timerDate });
  }

  private async refresh(): Promise<void> {
    try {
      const response = await fetch(`${window.location.origin}/oauth/refresh`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to refresh token: ${response.statusText}`);
      }

      this.load();
    } catch (err) {
      this.emit('error', err);
    }
  }

  get authorizationHeaderValue() {
    if (this.token) {
      return `Bearer ${this.token}`;
    }

    return undefined;
  }
}

type JWT = `${string}.${string}.${string}`;

function isPseudoJWT(str: string) {
  const segments = str.split('.');

  return (
    segments.length === 3 &&
    segments.every(segment => segment.length > 0) &&
    segments[2].length > 10
  );
}

function uniqueTokenId(jwt: JWT) {
  const [header, payload, signature] = jwt.split('.');

  const id = `${end(header)}${end(payload)}${end(signature)}`;
  return id;
}

function end(str: string) {
  return str.substr(str.length - 10);
}
